/**
 * Nia shared formatting utilities.
 *
 * Phase 9.4 — UI cleanup. Single source of truth for:
 *   * Currency code -> human-readable symbol mapping (KSh / USh)
 *   * Number formatting with thousands separators
 *   * Money rendering at the canonical three sizes (hero / summary / row)
 *
 * IMPORTANT — internal data vs. display:
 *   * `KES`, `UGX` are ISO-4217 codes used in DB rows, API payloads, and
 *     route values (<option value="KES">). DO NOT replace these.
 *   * `KSh`, `USh` are the *display* symbols rendered to humans. EVERY
 *     user-facing currency string must use these, never the ISO code.
 *
 * If you need to render money, prefer:
 *   - `formatCurrency(amount, code)`  -> "KSh 41,000"
 *   - `kes(amount)`                    -> back-compat shortcut for KES
 *   - The `<Money>` component, which applies the right typographic scale.
 */

// ---------------------------------------------------------------------------
// Currency tokens — single source of truth
// ---------------------------------------------------------------------------

/** ISO-4217 currency codes per supported recipient country. */
export const CURRENCY_CODES = Object.freeze({ KE: 'KES', UG: 'UGX' });

/**
 * Human-readable symbol per ISO currency code. Keep this list ALPHABETIZED
 * by code so new entries are obvious in code review.
 *   KES -> "KSh"   (Kenyan Shilling)
 *   UGX -> "USh"   (Ugandan Shilling)
 *
 * Unknown codes fall back to the code itself.
 */
export const CURRENCY_SYMBOLS = Object.freeze({
    KES: 'KSh',
    UGX: 'USh',
});

/** Convert an ISO currency code into the display symbol. */
export function currencySymbol(code) {
    if (!code) return CURRENCY_SYMBOLS.KES;
    return CURRENCY_SYMBOLS[code] || code;
}

// ---------------------------------------------------------------------------
// Number formatting
// ---------------------------------------------------------------------------

/**
 * Format an integer with English thousands separators ("41,000").
 * Always rounds toward zero and clamps null/NaN -> 0.
 */
export function formatNumber(n) {
    const v = Math.round(Number(n) || 0);
    return new Intl.NumberFormat('en-KE', { maximumFractionDigits: 0 }).format(v);
}

/**
 * Format an amount + ISO currency code into the canonical display string,
 * e.g. (5000, "KES") -> "KSh 5,000".
 *
 * Use `{ bare: true }` to skip the symbol (e.g. when the symbol is rendered
 * separately as a prefix badge inside a hero card).
 */
export function formatCurrency(amount, code = 'KES', opts = {}) {
    const num = formatNumber(amount);
    if (opts.bare) return num;
    return `${currencySymbol(code)} ${num}`;
}

/**
 * Back-compat shortcut. Prefer `formatCurrency` going forward.
 * Always renders as KSh because every legacy call site was rendering
 * KES-denominated amounts.
 */
export function kes(n, opts = {}) {
    if (n == null) return `${currencySymbol('KES')} 0`;
    return formatCurrency(n, 'KES', opts);
}

// ---------------------------------------------------------------------------
// Money typographic scale (matches `.nia-money-*` CSS utility classes)
// ---------------------------------------------------------------------------

/**
 * Canonical money type scale. Use these size names anywhere we render
 * money so we keep the visual scale consistent across all screens.
 *
 *   hero    -> 32px Playfair Display 700 — used for headline hero amounts
 *   summary -> 22px Playfair Display 700 — used for section totals
 *   row     -> 17px Playfair Display 700 — used for list rows
 */
export const MONEY_SIZE_PX = Object.freeze({
    hero: 32,
    summary: 22,
    row: 17,
});

export const MONEY_CLASS = Object.freeze({
    hero: 'nia-money nia-money-hero',
    summary: 'nia-money nia-money-summary',
    row: 'nia-money nia-money-row',
});

// ---------------------------------------------------------------------------
// Date helpers (unchanged from previous version)
// ---------------------------------------------------------------------------

export function shortDate(d) {
    if (!d) return '';
    const dt = typeof d === 'string' ? new Date(d) : d;
    return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function fullDate(d) {
    if (!d) return '';
    const dt = typeof d === 'string' ? new Date(d) : d;
    return dt.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

export function timeUntil(d) {
    if (!d) return '';
    const dt = typeof d === 'string' ? new Date(d) : d;
    const days = Math.ceil((dt.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
    if (days <= 0) return 'today';
    if (days === 1) return 'tomorrow';
    return `in ${days} days`;
}

export function relativeDate(d) {
    if (!d) return '';
    const dt = typeof d === 'string' ? new Date(d) : d;
    const days = Math.floor((Date.now() - dt.getTime()) / (1000 * 60 * 60 * 24));
    if (days < 1) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days} days ago`;
    return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function monthGroup(d) {
    if (!d) return '';
    const dt = typeof d === 'string' ? new Date(d) : d;
    return dt.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

/** Time-aware greeting that respects the user's actual local timezone. */
export function timeGreeting() {
    const h = new Date().getHours();
    if (h >= 5 && h < 12) return 'Good morning';
    if (h >= 12 && h < 17) return 'Good afternoon';
    if (h >= 17 && h < 22) return 'Good evening';
    return 'Good night';
}

// ---------------------------------------------------------------------------
// Country + phone helpers (unchanged)
// ---------------------------------------------------------------------------

export const COUNTRY = {
    KE: { flag: '🇰🇪', name: 'Kenya', dial: '+254' },
    UG: { flag: '🇺🇬', name: 'Uganda', dial: '+256' },
    CA: { flag: '🇨🇦', name: 'Canada', dial: '+1' },
    GB: { flag: '🇬🇧', name: 'United Kingdom', dial: '+44' },
    US: { flag: '🇺🇸', name: 'United States', dial: '+1' },
};

export function payoutLabel(method) {
    return ({ mpesa: 'M-Pesa', momo: 'MTN MoMo', mtn: 'MTN MoMo' }[method] || (method || '').toUpperCase());
}

export function payoutForCountry(country) {
    return country === 'UG' ? 'momo' : 'mpesa';
}

export function maskPhone(phone) {
    if (!phone) return '';
    const tail = phone.slice(-4);
    return phone.slice(0, 4) + ' •••• ' + tail;
}

/**
 * Normalize a raw phone to E.164 using the selected country.
 * Mirrors backend normalize_phone — country selection always overrides any embedded country code.
 */
export function normalizePhone(phone, country) {
    const raw = (phone || '').replace(/\D+/g, '');
    const prefixMap = { KE: '254', UG: '256', CA: '1', US: '1', GB: '44' };
    const prefix = prefixMap[country] || '';
    if (!raw) return '';
    if (raw.startsWith(prefix)) return `+${raw}`;
    const trimmed = raw.replace(/^0+/, '');
    return `+${prefix}${trimmed}`;
}
